import React from 'react';
import { Box, Button, useMediaQuery } from '@mui/material';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';

export interface RightNavigationButtonsProps {
  currentTime: Date;
  onTimeChange: (hours: number) => void;
  isMobile: boolean;
}

const RightNavigationButtons: React.FC<RightNavigationButtonsProps> = ({
  currentTime,
  onTimeChange,
  isMobile
}) => {
  // Media queries for responsive button visibility
  // Parent component (TimeNavigationControls) handles mobile vs desktop layout selection
  const isMobilePortrait = useMediaQuery('(orientation: portrait) and (max-width: 599px)');
  const isTabletPortrait = useMediaQuery('(orientation: portrait) and (min-width: 600px) and (max-width: 1023px)');

  // Always get the latest UTC time
  const getMaxTime = () => new Date();
  const MAX_TIME = getMaxTime();

  // Enable forward buttons if there's at least 1 minute of available time
  const MIN_TIME_TO_ENABLE = 60 * 1000;
  const canGoForward = (MAX_TIME.getTime() - currentTime.getTime()) > MIN_TIME_TO_ENABLE;

  const buttonStyles = isMobile
    ? {
        minWidth: '70px',
        px: 1,
        py: 0.5,
        fontSize: '0.9rem',
        '& .MuiSvgIcon-root': {
          fontSize: '1rem'
        }
      }
    : {
        minWidth: '90px',
        px: 1.2,
        py: 0.8,
        fontSize: '0.9rem',
        '& .MuiSvgIcon-root': {
          fontSize: '1.1rem'
        }
      };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobilePortrait ? 1 : 1.5 }}>
      {/* Forward 15 minutes - always show */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(0.25)}
        disabled={!canGoForward}
        variant="contained"
        endIcon={<KeyboardArrowRightIcon />}
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoForward ? 1 : 0.5
        }}
      >
        +15m
      </Button>

      {/* Forward 30 minutes - hide on mobile portrait and tablet portrait */}
      {!isMobilePortrait && !isTabletPortrait && (
        <Button
          className="no-uppercase"
          onClick={() => onTimeChange(0.5)}
          disabled={!canGoForward}
          variant="contained"
          endIcon={<KeyboardArrowRightIcon />}
          size={isMobile ? "small" : undefined}
          sx={{
            ...buttonStyles,
            opacity: canGoForward ? 1 : 0.5
          }}
        >
          +30m
        </Button>
      )}

      {/* Forward 1 hour - hide on mobile portrait only */}
      {!isMobilePortrait && (
        <Button
          className="no-uppercase"
          onClick={() => onTimeChange(1)}
          disabled={!canGoForward}
          variant="contained"
          endIcon={<KeyboardDoubleArrowRightIcon />}
          size={isMobile ? "small" : undefined}
          sx={{
            ...buttonStyles,
            opacity: canGoForward ? 1 : 0.5
          }}
        >
          +1h
        </Button>
      )}

      {/* Forward 4 hours - always show */}
      <Button
        className="no-uppercase"
        onClick={() => onTimeChange(4)}
        disabled={!canGoForward}
        variant="contained"
        size={isMobile ? "small" : undefined}
        sx={{
          ...buttonStyles,
          opacity: canGoForward ? 1 : 0.5
        }}
        endIcon={
          <Box sx={{ display: 'flex', alignItems: 'center', mr: -0.5 }}>
            <KeyboardArrowRightIcon />
            <KeyboardDoubleArrowRightIcon sx={{ ml: -1.2 }} />
          </Box>
        }
      >
        +4h
      </Button>
    </Box>
  );
};

export default RightNavigationButtons;